import Button, { Against, Kind, Size } from '@ux/button';
export { Kind, Size, Against } from '@ux/button';
import React, { Fragment } from 'react';
import pureEnhance from '@ux/pure-enhance';
import { defaultProps, withProps } from '@team-griffin/rehook';
import { opacify, modifier, withStylesHook } from '@ux/style-utils';
import Transition from 'react-inline-transition-group';
import { PulseLoader } from '@ux/penders';
import { createPaintbrushUrl } from '@ux/glyphs';
import { gu2px } from '@ux/theme-utils';
import reactCSS from 'reactcss';
import { propEq, cond, T, contains, always, anyPass, ifElse, either, prop } from 'ramda';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var Status;

(function (Status) {
  Status["DEFAULT"] = "DEFAULT";
  Status["PENDING"] = "PENDING";
  Status["SUCCESS"] = "SUCCESS";
  Status["FAILURE"] = "FAILURE";
})(Status || (Status = {}));

var PureReactiveButtonContent = function PureReactiveButtonContent(_ref) {
  var _contentMap;

  var styles = _ref.styles,
      theme = _ref.theme,
      status = _ref.status,
      children = _ref.children;
  var contentMap = (_contentMap = {}, _defineProperty(_contentMap, Status.DEFAULT, null), _defineProperty(_contentMap, Status.PENDING, React.createElement(PulseLoader, {
    key: "pulseLoader",
    theme: theme,
    color: styles.loader.color,
    size: styles.loader.size
  })), _defineProperty(_contentMap, Status.SUCCESS, //@ts-ignore
  React.createElement("img", {
    style: styles.icon,
    src: createPaintbrushUrl('smallTick', [styles.icon.color]),
    alt: "smallTick",
    loading: "lazy"
  })), _defineProperty(_contentMap, Status.FAILURE, //@ts-ignore
  React.createElement("img", {
    style: styles.icon,
    src: createPaintbrushUrl('smallCross', [styles.icon.color]),
    alt: "cross",
    loading: "lazy"
  })), _contentMap);
  return React.createElement("span", {
    key: "content"
  }, React.createElement("span", {
    key: "text",
    style: styles.text
  }, children), React.createElement(Transition, {
    key: "icon",
    style: styles.iconContainer,
    childrenStyles: {
      base: {
        opacity: 0
      },
      appear: {
        opacity: 1
      },
      enter: {
        transition: 'opacity 0.2s ease-in-out',
        opacity: 1
      },
      leave: {
        transition: 'opacity 0.2s ease-in-out',
        opacity: 0
      }
    }
  }, React.createElement("span", null, contentMap[status])));
};

var enhance = pureEnhance('ReactiveButtonContent');
var ReactiveButtonContent = enhance(PureReactiveButtonContent); // eslint-disable-next-line no-underscore-dangle

var getReactiveButtonStatus = function getReactiveButtonStatus(formProps) {
  var pristine = formProps.pristine,
      submitting = formProps.submitting,
      submitSucceeded = formProps.submitSucceeded,
      submitFailed = formProps.submitFailed,
      error = formProps.error,
      invalid = formProps.invalid;
  var status = Status.DEFAULT;

  if (pristine === true) {
    status = Status.DEFAULT;
  }

  if (submitting === true) {
    status = Status.PENDING;
  }

  if (submitSucceeded === true) {
    status = Status.SUCCESS;
  }

  if (submitFailed === true) {
    status = Status.FAILURE;
  }

  if (error === true) {
    status = Status.FAILURE;
  }

  if (invalid === true) {
    status = Status.FAILURE;
  }

  return status;
};
var getButtonKind = function getButtonKind(_ref) {
  var status = _ref.status,
      kind = _ref.kind,
      against = _ref.against;

  if (against === Against.PRIMARY) {
    return kind;
  }

  if (against === Against.HIGHLIGHT) {
    return kind;
  }

  if (status === Status.FAILURE) {
    return Kind.DETRIMENTAL;
  }

  if (status === Status.SUCCESS) {
    return Kind.POSITIVE;
  }

  return kind;
};

var kindPropKey = 'kind';
var againstPropKey = 'against';
var isKindPrimary = propEq(kindPropKey, Kind.PRIMARY);
var isKindSecondary = propEq(kindPropKey, Kind.SECONDARY);
var isKindTertiary = propEq(kindPropKey, Kind.TERTIARY);
var isKindHighlight = propEq(kindPropKey, Kind.HIGHLIGHT);
var isKindDetrimental = propEq(kindPropKey, Kind.DETRIMENTAL);
var isKindPositive = propEq(kindPropKey, Kind.POSITIVE);
var isAgainstPrimary = propEq(againstPropKey, Against.PRIMARY);
var isAgainstDark = propEq(againstPropKey, Against.DARK);
var isAgainstHighlight = propEq(againstPropKey, Against.HIGHLIGHT);

var isAgainst = function isAgainst(against, props) {
  return contains(props[againstPropKey], against);
};
/* eslint-disable max-len */


var colorMatrix = cond([[isKindPrimary, cond([[isAgainstPrimary, function (_ref) {
  var theme = _ref.theme;
  return theme.palette.primary;
}], [isAgainstHighlight, function (_ref2) {
  var theme = _ref2.theme;
  return theme.palette.highlight;
}], [T, function (_ref3) {
  var theme = _ref3.theme;
  return theme.palette.ctaContrast;
}]])], [isKindSecondary, cond([[isAgainstDark, function (_ref4) {
  var theme = _ref4.theme;
  return opacify(theme.palette.grey12, 0.85);
}], [isAgainstPrimary, function (_ref5) {
  var theme = _ref5.theme;
  return opacify(theme.palette.primaryContrast, 0.85);
}], [isAgainstHighlight, function (_ref6) {
  var theme = _ref6.theme;
  return opacify(theme.palette.highlightContrast, 0.85);
}], [T, function (_ref7) {
  var theme = _ref7.theme;
  return opacify(theme.palette.grey01, 0.85);
}]])], [isKindTertiary, cond([[isAgainstDark, function (_ref8) {
  var theme = _ref8.theme;
  return opacify(theme.palette.grey11, 0.85);
}], [isAgainstPrimary, function (_ref9) {
  var theme = _ref9.theme;
  return opacify(theme.palette.primaryContrast, 0.9);
}], [isAgainstHighlight, function (_ref10) {
  var theme = _ref10.theme;
  return opacify(theme.palette.highlightContrast, 0.9);
}], [T, function (_ref11) {
  var theme = _ref11.theme;
  return opacify(theme.palette.grey02, 0.85);
}]])], [isKindHighlight, cond([[isAgainstHighlight, function (_ref12) {
  var theme = _ref12.theme;
  return theme.palette.highlight;
}], [T, function (_ref13) {
  var theme = _ref13.theme;
  return theme.palette.highlightContrast;
}]])], [isKindDetrimental, cond([[function (props) {
  return isAgainst([Against.PRIMARY, Against.HIGHLIGHT], props);
}, always('#000')], [T, always('#fff')]])], [isKindPositive, cond([[function (props) {
  return isAgainst([Against.PRIMARY, Against.HIGHLIGHT], props);
}, always('#000')], [T, always('#fff')]])]]);
/* eslint-enable max-len */

var stylesheet = (function (props) {
  var _reactCSS;

  var theme = props.theme,
      kind = props.kind,
      status = props.status,
      against = props.against,
      size = props.size;
  var spacing = theme.spacing;
  var accentColor = colorMatrix(props);
  return reactCSS((_reactCSS = {
    default: {
      contentWrapper: {
        position: 'relative'
      },
      loader: {
        color: accentColor,
        size: gu2px(4, spacing)
      },
      text: {
        transition: 'opacity 0.2s ease-in-out'
      },
      glyph: {
        transition: 'opacity 0.2s ease-in-out'
      },
      iconContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      },
      icon: {
        animation: 'iconPulseIn 0.3s ease-in-out 1',
        display: 'block',
        width: gu2px(4, spacing),
        height: gu2px(4, spacing),
        color: accentColor
      }
    }
  }, _defineProperty(_reactCSS, modifier('size', Size.SM), {
    icon: {
      width: gu2px(3, spacing),
      height: gu2px(3, spacing)
    }
  }), _defineProperty(_reactCSS, modifier('size', Size.LG), {
    icon: {
      width: gu2px(5, spacing),
      height: gu2px(5, spacing)
    }
  }), _defineProperty(_reactCSS, modifier('status', Status.DEFAULT), {
    text: {
      opacity: 1
    }
  }), _defineProperty(_reactCSS, modifier('status', Status.PENDING), {
    root: {
      cursor: 'progress',
      opacity: 1
    },
    text: {
      opacity: 0.2
    },
    glyph: {
      opacity: 0.2
    }
  }), _defineProperty(_reactCSS, modifier('status', Status.SUCCESS), {
    root: {
      cursor: 'not-allowed',
      opacity: 1
    },
    text: {
      opacity: 0.001
    }
  }), _defineProperty(_reactCSS, modifier('status', Status.FAILURE), {
    text: {
      opacity: 0.001
    }
  }), _reactCSS), [{
    status: status
  }, {
    size: size
  }]);
});

var isDisabled = anyPass([propEq('disabled', true), propEq('status', Status.PENDING), propEq('status', Status.SUCCESS)]); // glyph should not show when we display the success tick

var getGlyph = ifElse(either(propEq('status', Status.SUCCESS), propEq('status', Status.FAILURE)), prop(null), prop('glyph'));

var PureReactiveButton = function PureReactiveButton(_a) {
  var theme = _a.theme,
      styles = _a.styles,
      status = _a.status,
      children = _a.children,
      buttonProps = __rest(_a, ["theme", "styles", "status", "children"]);

  return React.createElement(Fragment, null, React.createElement("style", null, "\n        @keyframes iconPulseIn {\n          0% { transform: scale(0); }\n          75% { transform: scale(1.1); }\n          100% { transform: scale(1); }\n        }\n      "), React.createElement(Button, Object.assign({
    theme: theme,
    styleOverrides: styles
  }, buttonProps), React.createElement("div", {
    style: styles.contentWrapper
  }, React.createElement(ReactiveButtonContent // @ts-ignore
  , {
    // @ts-ignore
    styles: styles,
    theme: theme,
    status: status
  }, children))));
};

var enhance$1 = pureEnhance('ReactiveButton', [defaultProps({
  status: Status.DEFAULT,
  size: Size.MD,
  against: Against.NEUTRAL
}), withProps(function (ownerProps) {
  return {
    disabled: isDisabled(ownerProps),
    kind: getButtonKind(ownerProps),
    glyph: getGlyph(ownerProps)
  };
}), withStylesHook(stylesheet)]);
var ReactiveButton = enhance$1(PureReactiveButton); // eslint-disable-next-line no-underscore-dangle

export default ReactiveButton;
export { Status, getReactiveButtonStatus };
